import { ConnectDragSource, ConnectDropTarget, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { useDispatch } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { DragSourceType } from 'const';
import { toggleDragState } from 'containers/App/actions';
import { dropReusableLayout, moveLayout } from 'containers/Layouts/actions';
import * as Models from 'models';
import { ArtboardGroupLayoutDragObject } from 'models/DragObjects/ArtboardGroupLayoutDragObject';

export const ALLOWED_TO_DROP_ITEMS: DragSourceType[] = [
  DragSourceType.GROUP_LAYOUT_REUSABLE_LAYOUT,
  DragSourceType.ARTBOARD_REUSABLE_LAYOUT,
  DragSourceType.REUSABLE_LAYOUT,
];

export const ALLOWED_TO_DROP_ITEMS_READ_ONLY: DragSourceType[] = [];

type GroupLayoutDragSourceHook = {
  connectDragSource: ConnectDragSource;
  isDragging: boolean;
};
const useGroupLayoutDragSource = (groupLayout: Models.GroupLayoutMap): GroupLayoutDragSourceHook => {
  const dispatch = useDispatch();
  const dragSourceType = DragSourceType.ARTBOARD_GROUP_LAYOUT;
  const [{ isDragging }, connectDragSource] = useDrag<ArtboardGroupLayoutDragObject, void, { isDragging: boolean }>({
    type: dragSourceType,
    item: () => {
      dispatch(toggleDragState(dragSourceType));

      return {
        layout: groupLayout,
      };
    },
    end: () => {
      dispatch(toggleDragState());
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return {
    connectDragSource,
    isDragging,
  };
};

type ItemObject = Models.ArtboardLayoutDragObject | Models.LayoutDragObject;
const dropFn = (
  sectionId: string,
  item: ItemObject,
  monitor: DropTargetMonitor,
  dispatch: Dispatch<AnyAction>,
): void => {
  const itemType = monitor.getItemType() as DragSourceType;

  switch (itemType) {
    case DragSourceType.GROUP_LAYOUT_REUSABLE_LAYOUT:
    case DragSourceType.ARTBOARD_REUSABLE_LAYOUT: {
      const { layout } = item as Models.ArtboardLayoutDragObject;
      dispatch(moveLayout(layout.get('id'), sectionId));
      break;
    }
    case DragSourceType.REUSABLE_LAYOUT: {
      const { storyCardId, document } = item as Models.LayoutDragObject;
      const reusableLayout = document as Models.ReusableLayoutMap;
      dispatch(dropReusableLayout(reusableLayout.get('id'), sectionId, storyCardId));
      break;
    }
  }
};

type GroupLayoutDropTargetHook = {
  connectDropTarget: ConnectDropTarget;
  isOver: boolean;
  isOverByAllowedItem: boolean;
};
const useGroupLayoutDropTarget = (isLayoutBeingUploaded: boolean, sectionId: string, isReadOnly: boolean): GroupLayoutDropTargetHook => {
  const dispatch = useDispatch();
  const allowedToDropItems: DragSourceType[] = isReadOnly ? ALLOWED_TO_DROP_ITEMS_READ_ONLY : ALLOWED_TO_DROP_ITEMS;
  const [{ isOver, dragItemType }, connectDropTarget] = useDrop({
    accept: allowedToDropItems,
    drop: (item: ItemObject, monitor) => dropFn(sectionId, item, monitor, dispatch),
    canDrop: (_, monitor: DropTargetMonitor) => {
      const itemType = monitor.getItemType() as DragSourceType;

      return !isLayoutBeingUploaded && allowedToDropItems.includes(itemType);
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      dragItemType: monitor.getItemType() as DragSourceType,
    }),
  });
  const isOverByAllowedItem = isOver && ALLOWED_TO_DROP_ITEMS.includes(dragItemType);

  return {
    connectDropTarget,
    isOver,
    isOverByAllowedItem,
  };
};

type GroupLayoutDnDHook = GroupLayoutDragSourceHook & GroupLayoutDropTargetHook;
type Props = {
  groupLayout: Models.GroupLayoutMap;
  isLayoutBeingUploaded: boolean;
  sectionId: string;
  isReadOnly: boolean;
};
export const useGroupLayoutDnD = (props: Props): GroupLayoutDnDHook => {
  const { groupLayout, isLayoutBeingUploaded, sectionId, isReadOnly } = props;
  const { connectDragSource, isDragging } = useGroupLayoutDragSource(groupLayout);
  const { connectDropTarget, isOver, isOverByAllowedItem } = useGroupLayoutDropTarget(isLayoutBeingUploaded, sectionId, isReadOnly);

  return {
    connectDragSource,
    isDragging,
    connectDropTarget,
    isOver,
    isOverByAllowedItem,
  };
};
